/* eslint-disable max-lines */
import { motion } from 'framer-motion';
import cn from 'classnames';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useCommentsContext } from '@components/editors/shared/comments/comments-context.tsx';
import {
  Bold,
  ChevronDown,
  Eraser,
  Highlighter,
  Italic,
  MessageCircle,
  Strikethrough,
  Tag as TagIcon,
  Underline
} from 'lucide-react';
import TagPickerModal from '@components/tags/tag-picker-modal.tsx';
import { useTagAssignments } from '@utils/providers/tag-assignments-context.tsx';
import { useDeleteTagAssignment } from '@utils/api/tag-assignments';
import { createPortal } from 'react-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';

/* eslint-disable no-restricted-syntax -- Semantic editor highlight/playback colors must stay constant across themes for marker recognizability */

const HIGHLIGHT_COLORS = [
  { color: '#FEE047', label: 'highlight-yellow' },
  { color: '#F9A8D4', label: 'highlight-pink' },
  { color: '#86EFAC', label: 'highlight-green' }
] as const;

interface Props {
  containerRef: RefObject<HTMLDivElement>;
  popoverOpen: boolean | undefined;
  sessionId: string;
  documentId: string;
}

export const TranscriptTextStyleBox = ({
  containerRef,
  popoverOpen,
  sessionId,
  documentId
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { applyStyle, startStopWord, setPopoverOpen } = useTranscript();
  const commentsCtx = useCommentsContext();
  const [isTagPickerOpen, setIsTagPickerOpen] = useState<boolean>(false);
  const [tagSelectedText, setTagSelectedText] = useState<string>('');
  const [lastUsedColor, setLastUsedColor] = useState<string>(
    HIGHLIGHT_COLORS[0].color
  );
  const [isHighlightDropdownOpen, setIsHighlightDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const highlightButtonRef = useRef<HTMLDivElement>(null);
  const dropdownToggleRef = useRef<HTMLButtonElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const { findAssignmentForRange } = useTagAssignments();
  const deleteAssignment = useDeleteTagAssignment();

  const existingAssignment =
    startStopWord != null
      ? findAssignmentForRange(startStopWord.start, startStopWord.stop)
      : undefined;

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isHighlightDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideDropdown =
        dropdownMenuRef.current && !dropdownMenuRef.current.contains(target);
      const isOutsideToggle =
        dropdownToggleRef.current &&
        !dropdownToggleRef.current.contains(target);

      if (isOutsideDropdown && isOutsideToggle) {
        setIsHighlightDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isHighlightDropdownOpen]);

  const openCompose = () => {
    if (!startStopWord) return;
    // #2244: open a draft card inline in the gutter at the current
    // selection's anchor — no modal in the comment path. Each pane
    // hosts its own CommentsProvider (source vs translation in split
    // view), so `commentsCtx` is scoped to this pane's document — no
    // cross-pane leakage possible here.
    commentsCtx?.startDraft({
      indexFrom: String(startStopWord.start),
      indexTo: String(startStopWord.stop)
    });
    setPopoverOpen(false);
  };

  const handleHighlightClick = () => {
    applyStyle('mark', lastUsedColor);
  };

  const handleColorSelect = (color: string) => {
    setLastUsedColor(color);
    applyStyle('mark', color);
    setIsHighlightDropdownOpen(false);
  };

  const handleDropdownToggle = () => {
    if (!isHighlightDropdownOpen && dropdownToggleRef.current) {
      const rect = dropdownToggleRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left - 40
      });
    }
    setIsHighlightDropdownOpen(!isHighlightDropdownOpen);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative transition-shadow duration-300 border ',
        {
          'rounded-2xl border border-gray-200 dark:border-zinc-700': popoverOpen
        },
        'border-transparent'
      )}
      style={{
        display: 'block',
        maxWidth: 'fit-content'
      }}
    >
      <motion.div
        transition={{ duration: 0.5 }}
        className="border-gray-200 dark:border-zinc-700"
      >
        <div className="flex gap-1 w-full h-8 items-center">
          <Tooltip label={shortcutLabel(t('bold'), 'Ctrl+B')}>
            <button
              onClick={() => applyStyle('bold')}
              aria-label={t('bold')}
              className="h-8 w-8 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('icon')
              }}
            >
              <Bold size={14} aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip label={shortcutLabel(t('italic'), 'Ctrl+I')}>
            <button
              onClick={() => applyStyle('italic')}
              aria-label={t('italic')}
              className="h-8 w-8 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('icon')
              }}
            >
              <Italic size={14} aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip label={shortcutLabel(t('underline'), 'Ctrl+U')}>
            <button
              onClick={() => applyStyle('underlined')}
              aria-label={t('underline')}
              className="h-8 w-8 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('icon')
              }}
            >
              <Underline size={14} aria-hidden="true" />
            </button>
          </Tooltip>
          <Tooltip label={shortcutLabel(t('strikethrough'), 'Ctrl+Shift+X')}>
            <button
              onClick={() => applyStyle('strike')}
              aria-label={t('strikethrough')}
              className="h-8 w-8 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('icon')
              }}
            >
              <Strikethrough size={14} aria-hidden="true" />
            </button>
          </Tooltip>

          <Tooltip label={t('add-comment')}>
            <button
              onClick={() => {
                if (!startStopWord) {
                  toast.error(t('please-select-the-text-to-add-comment-to'));
                  return;
                }
                openCompose();
              }}
              aria-label={t('add-comment')}
              className="h-8 w-8 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('icon')
              }}
            >
              <MessageCircle size={14} aria-hidden="true" />
            </button>
          </Tooltip>

          <Tooltip
            label={
              existingAssignment
                ? `${t('remove-tag') || 'Remove tag'}: ${existingAssignment.tag_name}`
                : t('tag-selection-tooltip') ||
                  'Label this selection with a tag. Tagged sections appear grouped in the export.'
            }
          >
            <button
              onClick={() => {
                if (!startStopWord) {
                  toast.error(
                    t('please-select-the-text-to-tag') ||
                      'Select text first to apply a tag.'
                  );
                  return;
                }
                if (existingAssignment) {
                  deleteAssignment.mutate(
                    {
                      session_id: sessionId,
                      document_id: documentId,
                      assignment_id: String(existingAssignment.id)
                    },
                    {
                      onSuccess: () => {
                        toast.success(t('tag-removed') || 'Tag removed');
                        setPopoverOpen(false);
                      },
                      onError: () => {
                        toast.error(
                          t('failed-to-remove-tag') || 'Failed to remove tag'
                        );
                      }
                    }
                  );
                  return;
                }
                const text = window.getSelection()?.toString().trim() || '';
                setTagSelectedText(text);
                setIsTagPickerOpen(true);
              }}
              aria-label={
                existingAssignment
                  ? `${t('remove-tag') || 'Remove tag'}: ${existingAssignment.tag_name}`
                  : t('tag-selection') || t('tag') || 'Tag selection'
              }
              className="h-8 w-8 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: existingAssignment
                  ? existingAssignment.tag_color || getColour('icon')
                  : getColour('icon')
              }}
            >
              <TagIcon size={14} aria-hidden="true" />
            </button>
          </Tooltip>

          {/* Highlight — single split-button control: click applies the
              last-used color; chevron opens color swatches + remove option. */}
          <div
            ref={highlightButtonRef}
            className="relative inline-flex items-center rounded-2xl border overflow-hidden shrink-0"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('thirdLayer')
            }}
          >
            <Tooltip label={t('highlight')}>
              <button
                onClick={handleHighlightClick}
                aria-label={t('highlight')}
                className="h-8 w-8 flex items-center justify-center transition-colors hover:opacity-80"
                style={{ color: getColour('icon') }}
              >
                <Highlighter
                  size={14}
                  style={{ color: lastUsedColor }}
                  aria-hidden="true"
                />
              </button>
            </Tooltip>
            <div
              className="w-px h-5"
              style={{ backgroundColor: getColour('border') }}
            />
            <Tooltip label={t('more-colors')}>
              <button
                ref={dropdownToggleRef}
                onClick={handleDropdownToggle}
                aria-label={t('more-colors')}
                aria-expanded={isHighlightDropdownOpen}
                aria-haspopup="menu"
                className="h-8 w-6 flex items-center justify-center transition-colors hover:opacity-80"
                style={{ color: getColour('icon') }}
              >
                <ChevronDown size={14} aria-hidden="true" />
              </button>
            </Tooltip>
            {isHighlightDropdownOpen &&
              createPortal(
                <div
                  ref={dropdownMenuRef}
                  className="fixed p-2 rounded-2xl border shadow-lg flex flex-col gap-1.5"
                  style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    backgroundColor: getColour('thirdLayer'),
                    borderColor: getColour('border'),
                    zIndex: 9999
                  }}
                >
                  <div className="flex gap-1.5 px-0.5">
                    {HIGHLIGHT_COLORS.map(({ color, label }) => (
                      <Tooltip key={color} label={t(label)}>
                        <button
                          onClick={() => handleColorSelect(color)}
                          aria-label={t(label)}
                          aria-pressed={lastUsedColor === color}
                          className={cn(
                            'w-5 h-5 rounded-full transition-all duration-200 cursor-pointer hover:scale-110',
                            lastUsedColor === color &&
                              'ring-2 ring-offset-1 ring-gray-200'
                          )}
                          style={{
                            backgroundColor: color,
                            border: `1px solid ${getColour('border')}`
                          }}
                        />
                      </Tooltip>
                    ))}
                  </div>
                  <div
                    className="h-px w-full"
                    style={{ backgroundColor: getColour('border') }}
                  />
                  <button
                    onClick={() => {
                      applyStyle('unmark');
                      setIsHighlightDropdownOpen(false);
                    }}
                    className="flex items-center gap-2 px-2 py-1 rounded-lg text-xs transition-colors hover:opacity-80"
                    style={{ color: getColour('normalText') }}
                  >
                    <Eraser size={14} style={{ color: getColour('icon') }} />
                    {t('remove-highlight')}
                  </button>
                </div>,
                document.body
              )}
          </div>
        </div>
      </motion.div>

      {isTagPickerOpen && (
        <TagPickerModal
          isOpen={isTagPickerOpen}
          onClose={() => {
            setIsTagPickerOpen(false);
            setPopoverOpen(false);
          }}
          sessionId={sessionId}
          documentId={documentId}
          selectedText={tagSelectedText}
          indexFrom={startStopWord?.start.toString()}
          indexTo={startStopWord?.stop.toString()}
        />
      )}

      {/* Comment compose is handled by `CommentsGutter`: it mounts an
          inline edit card at the selection anchor as soon as
          `startDraft` sets the draft. No modal in the comment path
          (#2244). */}
    </div>
  );
};
